/**
 * PartnerOrgBadge — gold pill rendered on org pages once an organization
 * has earned BAM Partner status (6+ months of continuous active billing,
 * or admin-set via admin_set_org_partner).
 *
 * Distinct from BamCoachBadge (which marks an individual person as staff)
 * and CertBadge (which marks a person as exam-certified). Partner Org is
 * org-scoped: it lives on the org page, not on individual coach names.
 *
 * In Phase 1, render this on:
 *   - /org dashboard header
 *   - Admin OrgManagement list rows
 *   - (future) coach profile cards when their primary org is a partner
 *
 * Usage:
 *   <PartnerOrgBadge size="md" />
 *   <PartnerOrgBadge size="sm" since={org.partner_since} />
 */
import React, { useState } from "react";
import { GOLD } from "../constants/theme";

export function PartnerOrgBadge({ size = "md", since, tooltip = true, style }) {
  const [hover, setHover] = useState(false);

  const isMd = size === "md";
  const sinceLabel = since ? formatPartnerSince(since) : null;

  return (
    <span
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label="BAM Partner Organization"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: isMd ? 6 : 4,
        padding: isMd ? "4px 11px" : "2px 8px",
        borderRadius: 999,
        background: "transparent",
        color: GOLD,
        border: `1.5px solid ${GOLD}`,
        fontSize: isMd ? 11 : 9.5,
        fontWeight: 800,
        fontFamily: "'Bebas Neue',sans-serif",
        letterSpacing: isMd ? 1.4 : 1.0,
        lineHeight: 1.3,
        flexShrink: 0,
        verticalAlign: "middle",
        cursor: tooltip ? "help" : "default",
        position: "relative",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      <svg
        width={isMd ? 11 : 9}
        height={isMd ? 11 : 9}
        viewBox="0 0 12 12"
        aria-hidden="true"
      >
        <path
          d="M6 0.8 L7.45 4.6 L11.4 4.85 L8.35 7.4 L9.35 11.2 L6 8.95 L2.65 11.2 L3.65 7.4 L0.6 4.85 L4.55 4.6 Z"
          fill={GOLD}
        />
      </svg>
      BAM PARTNER ORG

      {tooltip && hover && (
        <span
          role="tooltip"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            background: "#111",
            color: "#fff",
            fontSize: 10,
            fontWeight: 700,
            padding: "5px 9px",
            borderRadius: 5,
            whiteSpace: "nowrap",
            zIndex: 9999,
            letterSpacing: 0.2,
            fontFamily: "'DM Sans',sans-serif",
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            pointerEvents: "none",
          }}
        >
          {sinceLabel ? `Partner since ${sinceLabel}` : "BAM Partner Organization"}
        </span>
      )}
    </span>
  );
}

function formatPartnerSince(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

export default PartnerOrgBadge;
